// ── Progress Tracker ──────────────────────────────────────────────────────────
// localStorage is the synchronous source of truth at runtime (instant reads,
// works offline). Every mutation also schedules a debounced Firestore upload
// via progress-sync so the same account stays consistent across devices.

import { scheduleFirestoreWrite } from "./progress-sync";

const STORAGE_KEY = "samajh-progress";

function getProgress() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
}

function saveProgress(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
  // Fires only when a uid has been set via setSyncUid (i.e. signed in).
  scheduleFirestoreWrite();
}

// Track chapter visit
export function trackChapterVisit(cls, subject, chapter) {
  const p = getProgress();
  const key = `${cls}-${subject}-${chapter}`;
  if (!p.chapters) p.chapters = {};
  if (!p.chapters[key]) {
    p.chapters[key] = { cls, subject, chapter, firstVisit: new Date().toISOString(), visits: 0, completed: false };
  }
  p.chapters[key].visits++;
  p.chapters[key].lastVisit = new Date().toISOString();
  saveProgress(p);
}

// Track chapter completion
export function trackChapterComplete(cls, subject, chapter, rating) {
  const p = getProgress();
  const key = `${cls}-${subject}-${chapter}`;
  if (!p.chapters) p.chapters = {};
  if (!p.chapters[key]) {
    p.chapters[key] = { cls, subject, chapter, firstVisit: new Date().toISOString(), visits: 1 };
  }
  p.chapters[key].completed = true;
  p.chapters[key].completedAt = new Date().toISOString();
  if (rating) p.chapters[key].rating = rating;
  saveProgress(p);
}

// Track daily activity
export function trackDailyActivity() {
  const p = getProgress();
  const today = new Date().toISOString().slice(0, 10);
  if (!p.activeDays) p.activeDays = [];
  if (!p.activeDays.includes(today)) {
    p.activeDays.push(today);
  }
  saveProgress(p);
}

// Get dashboard stats — filtered by class
export function getDashboardStats(filterClass) {
  const p = getProgress();
  const chapters = p.chapters || {};
  const activeDays = p.activeDays || [];

  // Filter by class if provided
  const filtered = filterClass
    ? Object.values(chapters).filter(c => String(c.cls) === String(filterClass))
    : Object.values(chapters);

  const completed = filtered.filter(c => c.completed);
  const visited = filtered;
  const totalConcepts = completed.length;

  // Recently explored — sorted by lastVisit
  const recent = [...visited]
    .sort((a, b) => (b.lastVisit || "").localeCompare(a.lastVisit || ""))
    .slice(0, 5);

  // Subjects with progress
  const subjects = {};
  for (const ch of visited) {
    if (!subjects[ch.subject]) subjects[ch.subject] = { total: 0, completed: 0 };
    subjects[ch.subject].total++;
    if (ch.completed) subjects[ch.subject].completed++;
  }

  return {
    conceptsExplored: totalConcepts,
    daysActive: activeDays.length,
    activeDaysList: activeDays,
    chaptersVisited: visited.length,
    chaptersCompleted: completed.length,
    recentChapters: recent,
    subjectProgress: subjects,
    allChapters: chapters, // raw chapter data keyed by "cls-subject-chapter"
  };
}

// Track micro-quiz result per concept
export function trackQuizResult(cls, subject, chapter, conceptName, totalQs, correctQs) {
  const p = getProgress();
  if (!p.conceptScores) p.conceptScores = {};
  const key = `${cls}-${subject}-${conceptName}`;
  p.conceptScores[key] = {
    cls, subject, chapter, concept: conceptName,
    total: totalQs, correct: correctQs,
    score: totalQs > 0 ? Math.round((correctQs / totalQs) * 100) : 0,
    timestamp: new Date().toISOString(),
  };
  saveProgress(p);
}

// Get strong and weak concepts for a class
export function getStrengthWeakness(filterClass) {
  const p = getProgress();
  const scores = p.conceptScores || {};
  const filtered = filterClass
    ? Object.values(scores).filter(s => String(s.cls) === String(filterClass))
    : Object.values(scores);

  const strong = filtered.filter(s => s.score >= 80).sort((a, b) => b.score - a.score).slice(0, 5);
  const weak = filtered.filter(s => s.score < 60).sort((a, b) => a.score - b.score).slice(0, 5);

  return { strong, weak };
}
export function getRevisitSuggestions(filterClass) {
  const p = getProgress();
  const chapters = p.chapters || {};
  const filtered = filterClass
    ? Object.values(chapters).filter(c => String(c.cls) === String(filterClass))
    : Object.values(chapters);
  return filtered
    .filter(c => !c.completed || (c.rating && c.rating <= 2))
    .sort((a, b) => (a.lastVisit || "").localeCompare(b.lastVisit || ""))
    .slice(0, 3);
}

// Compute consecutive streak days ending today
export function getStreakDays(activeDaysList) {
  if (!activeDaysList || activeDaysList.length === 0) return 0;
  const sorted = [...activeDaysList].sort().reverse();
  const today = new Date().toISOString().slice(0, 10);
  // If most recent day isn't today, streak is 0
  if (sorted[0] !== today) return 0;
  let streak = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const curr = new Date(sorted[i]);
    const diffMs = prev.getTime() - curr.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) streak++;
    else break;
  }
  return streak;
}

// Count concepts in a chapter that have at least one quiz attempt — used by chapter list to show real progress
export function getChapterConceptsAttempted(filterClass, subject, chapter) {
  const p = getProgress();
  const scores = p.conceptScores || {};
  return Object.values(scores).filter(s =>
    String(s.cls) === String(filterClass) &&
    s.subject === subject &&
    s.chapter === chapter
  ).length;
}

// Count concepts with score >= 70 for a given class
export function getConceptsMastered(filterClass) {
  const p = getProgress();
  const scores = p.conceptScores || {};
  const filtered = filterClass
    ? Object.values(scores).filter(s => String(s.cls) === String(filterClass))
    : Object.values(scores);
  return filtered.filter(s => s.score >= 70).length;
}
